import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Ban } from 'lucide-react';
import { Badge, Button, useToast } from '../../../components/ui/index.js';
import { Can } from '../../../components/auth/Can.js';
import { useDaemonScope } from './DaemonScopeContext.js';
import { daemonKeys, daemonReads, daemonWrites } from './daemon-queries.js';
import { RunGrant, asText, unwrapListResponse } from './run-audit-shared.js';

export const PermissionGrantsPanel: React.FC<{ daemonId: number }> = ({ daemonId }) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { scope, policy } = useDaemonScope();

  const grantsQuery = useQuery(daemonReads(scope).grants(daemonId, { status: 'active', limit: '50' }));

  const revokeMutation = useMutation({
    ...daemonWrites(scope).revokeGrant(),
    onSuccess: () => {
      notify('Permission revoked');
      queryClient.invalidateQueries({ queryKey: daemonKeys(scope).grants(daemonId) });
    },
    onError: () => notify('Could not revoke the permission', 'danger'),
  });
  const revoke = (grantId: number) =>
    revokeMutation.mutate({
      path: { id: String(daemonId), grantId: String(grantId) },
      body: { reason: 'Revoked from daemon permissions panel' },
    });

  const grants = unwrapListResponse<RunGrant>(grantsQuery.data);
  if (grantsQuery.isLoading) return <div className="text-xs text-muted">Loading active permission grants...</div>;
  if (grantsQuery.isError) return <div className="text-xs text-danger">Could not load permission grants.</div>;
  if (grants.length === 0) return <div className="text-xs text-muted">No active scoped permission grants.</div>;

  return (
    <div className="rounded-sm border bg-surface p-3">
      <div className="flex items-center gap-2 text-sm font-medium text-body mb-2">
        <KeyRound size={14} className="text-accent-text" />
        Active Permission Grants
        <Badge variant="neutral" size="sm">
          {grants.length}
        </Badge>
      </div>
      <div className="space-y-2">
        {grants.map((grant) => (
          <div
            key={grant.id}
            className="rounded-sm border bg-surface-alt p-2 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2"
          >
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-body">{asText(grant.action)}</span>
                <Badge variant={grant.grantMode === 'always' ? 'warning' : 'info'} size="sm">
                  {asText(grant.grantMode)}
                </Badge>
                <Badge variant="neutral" size="sm">
                  {asText(grant.resourceType)}
                </Badge>
              </div>
              <div className="text-xs text-muted mt-1 break-all">{asText(grant.resourceId)}</div>
              <div className="text-xs text-muted mt-1">
                Granted by {asText(grant.grantedBy) || 'system'}
                {grant.expiresAt ? ` / expires ${new Date(asText(grant.expiresAt)).toLocaleString()}` : ''}
              </div>
            </div>
            <Can policy={policy}>
              <Button size="sm" variant="secondary" loading={revokeMutation.isPending} onClick={() => revoke(grant.id)}>
                <Ban size={14} />
                Revoke
              </Button>
            </Can>
          </div>
        ))}
      </div>
    </div>
  );
};
